import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./DownloadsPage.module.scss";

import iPadGraphicLightMode from "assets/download-page-ipad-graphic.png";
import iPadGraphicDarkMode from "assets/download-page-ipad-graphic-darkmode.png";
import QRStudio from "assets/icons/switcher-qr.png";
import QRCamAndroid from "assets/icons/cam-android-qr.png";
import QRCamIos from "assets/icons/cam-ios-qr.png";
import Apple from "assets/icons/apple.svg?react";
import Windows from "assets/icons/windows.svg?react";
import RemoteCameraIcon from "assets/icons/remote-camera.svg?react";
import ScreenshareIcon from "assets/icons/screenshare.svg?react";
import AndroidIcon from "assets/icons/android.svg?react";
import { useTheme } from "hooks/useTheme";
import { ThemeOptions } from "store/view/types";
import { ModalBase } from "components/modal/ModalBase";

export const DownloadsPage: React.FC = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const [selectedQrPlatform, setSelectedQrPlatform] = React.useState<
        "ios" | "android" | null
    >(null);
    const [isQrModalOpen, setIsQrModalOpen] = React.useState(false);

    const isIosSelected = selectedQrPlatform === "ios";
    const selectedQrImage = isIosSelected ? QRCamIos : QRCamAndroid;
    const selectedQrAlt = isIosSelected
        ? t("downloads:ios-qr-alt")
        : t("downloads:android-qr-placeholder-alt");
    const qrModalHeader = isIosSelected
        ? t("downloads:scan-code-for-ios")
        : t("downloads:scan-code-for-android");

    const openQrModal = React.useCallback((platform: "ios" | "android") => {
        setSelectedQrPlatform(platform);
        setIsQrModalOpen(true);
    }, []);

    const handleCloseQrModal = React.useCallback(() => {
        setSelectedQrPlatform(null);
    }, []);

    return (
        <div className={styles["page"]}>
            <header className={styles["header"]}>
                <p>{t("downloads:page-description")}</p>
            </header>

            <section className={styles["hero"]}>
                <img
                    src={
                        theme === ThemeOptions.Light
                            ? iPadGraphicLightMode
                            : iPadGraphicDarkMode
                    }
                    alt="Switcher Studio app preview"
                    className={styles["hero-graphic"]}
                />

                <div className={styles["hero-content"]}>
                    <h3>{t("downloads:hero-title")}</h3>
                    <p className={styles["hero-description"]}>
                        <span className={styles["desktop-only"]}>
                            {t("downloads:hero-description")}
                        </span>
                        <span className={styles["mobile-only"]}>
                            {t("downloads:hero-description-mobile")}
                        </span>
                    </p>

                    <a
                        title={t("downloads:download-for-mac")}
                        href="https://apps.apple.com/us/app/switcher-live-streaming-app/id908386221"
                        className={styles["primary-cta"]}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Apple aria-hidden="true" />
                        <span className={styles["desktop-only"]}>
                            {t("downloads:download-for-mac")}
                        </span>
                        <span className={styles["mobile-only"]}>
                            {t("downloads:download-for-ios")}
                        </span>
                    </a>

                    <p className={styles["hero-mobile-note"]}>
                        {t("downloads:hero-mobile-note")}
                    </p>

                    <div className={styles["hero-qr"]}>
                        <p>
                            <Apple aria-hidden="true" />
                            <span>{t("downloads:ios-label")}</span>
                        </p>
                        <img src={QRStudio} alt={t("downloads:ios-qr-alt")} />
                    </div>
                </div>
            </section>

            <section className={styles["cards-grid"]}>
                <article className={styles["card"]}>
                    <div className={styles["card-header"]}>
                        <span className={styles["card-icon"]}>
                            <RemoteCameraIcon aria-hidden="true" />
                        </span>
                        <div>
                            <h4>{t("downloads:remote-card-title")}</h4>
                            <p className={styles["card-subtitle"]}>
                                {t("downloads:remote-card-subtitle")}
                            </p>
                        </div>
                    </div>
                    <p className={styles["card-copy"]}>
                        {t("downloads:remote-card-description")}
                    </p>

                    <div className={styles["card-qr-row"]}>
                        <div className={styles["card-qr-actions"]}>
                            <button
                                type="button"
                                onClick={() => openQrModal("ios")}
                                className={`${styles["outline-cta"]} ${styles["card-qr-action"]}`}
                            >
                                <Apple aria-hidden="true" />
                                <span>{t("downloads:scan-code-for-ios")}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => openQrModal("android")}
                                className={`${styles["outline-cta"]} ${styles["card-qr-action"]}`}
                            >
                                <AndroidIcon aria-hidden="true" />
                                <span>{t("downloads:scan-code-for-android")}</span>
                            </button>
                        </div>
                    </div>

                    <div className={styles["mobile-actions"]}>
                        <a
                            href="https://apps.apple.com/us/app/switcher-live-streaming-app/id908386221"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles["outline-cta"]}
                            title={t("downloads:download-for-ios")}
                        >
                            <Apple aria-hidden="true" />
                            <span>{t("downloads:download-for-ios")}</span>
                        </a>
                        <a
                            href="https://play.google.com/store/apps/details?id=com.switcherstudio.capandroid"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles["outline-cta"]}
                            title={t("downloads:download-for-android")}
                        >
                            <AndroidIcon aria-hidden="true" />
                            <span>{t("downloads:download-for-android")}</span>
                        </a>
                    </div>
                </article>

                <article className={styles["card"]}>
                    <div className={styles["card-header"]}>
                        <span className={styles["card-icon"]}>
                            <ScreenshareIcon aria-hidden="true" />
                        </span>
                        <div>
                            <h4>{t("downloads:screenshare-card-title")}</h4>
                            <p className={styles["card-subtitle"]}>
                                {t("downloads:screenshare-card-subtitle")}
                            </p>
                        </div>
                    </div>
                    <p
                        className={`${styles["card-copy"]} ${styles["screenshare-copy"]}`}
                    >
                        {t("downloads:screenshare-card-description")}
                    </p>

                    <div className={styles["desktop-actions"]}>
                        <a
                            href="https://itunes.apple.com/us/app/switcher-cast/id1238109306?mt=12"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles["outline-cta"]}
                            title={t("downloads:download-for-mac")}
                        >
                            <Apple aria-hidden="true" />
                            <span>{t("downloads:download-for-mac")}</span>
                        </a>
                        <a
                            href="https://switcherstudio.blob.core.windows.net/staticcontent/software/SwitcherCastWindows.zip"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles["outline-cta"]}
                            title={t("downloads:download-for-windows")}
                        >
                            <Windows aria-hidden="true" />
                            <span>{t("downloads:download-for-windows")}</span>
                        </a>
                    </div>

                    <p className={styles["screenshare-mobile-note"]}>
                        {t("downloads:screenshare-mobile-note")}
                    </p>
                </article>
            </section>

            <ModalBase
                isOpen={isQrModalOpen}
                setIsOpen={setIsQrModalOpen}
                onDismiss={handleCloseQrModal}
                shouldHideButtons
                wrapperClassName={styles["qr-modal-wrapper"]}
                header={qrModalHeader}
            >
                {selectedQrPlatform && (
                    <div className={styles["qr-modal-content"]}>
                        <img src={selectedQrImage} alt={selectedQrAlt} />
                    </div>
                )}
            </ModalBase>
        </div>
    );
};
